import React, { useCallback, useContext, useState } from 'react';
import { ModalWrapper } from 'components/ModalWindows/ModalWrapper';
import { ModalToConfirm } from 'components/ModalWindows/ModalToConfirm';
import ModalSentSuccessfully from 'components/ModalWindows/ModalSentSuccessfully';
import ModalSentError from 'components/ModalWindows/ModalSentError';
import ModalSentErrorWithInfoForComboGroup from 'components/ModalWindows/ModalSentErrorWithInfoForComboGroup';
import { convertUndefinedFields } from 'utils/helpers';
import axios from 'axios';
import { ServerErrorTypes } from 'types';
import { AppContext, IAppContext } from 'appState/appContext';
import { CreateComboGroupContext, ICreateComboGroupContext } from '../createComboGroupState/createComboGroupContext';
import { createComboGroup } from 'backend/comboGroups';

export const ModalWindows: React.FC = () => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { resetFields, changeTypeOfModalWindow, dataForSubmit, typeOfModalWindow, fetchedDishes, fetchedComboItems } =
		useContext<ICreateComboGroupContext>(CreateComboGroupContext);
	const [dataForModalWithInfo, setDataForModalWithInfo] = useState<{
		translationKey: string;
		names: string[];
	} | null>(null);

	const handleReset = useCallback(() => {
		resetFields();
		changeTypeOfModalWindow(null);
	}, []);

	const handleConfirm = useCallback(async () => {
		if (dataForSubmit) {
			try {
				changeTypeOfModalWindow(null);
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await createComboGroup(convertedData);
				changeTypeOfModalWindow('success');
			} catch (err) {
				if (
					axios.isAxiosError(err) &&
					err.response?.data.Type === ServerErrorTypes.COMBO_GROUP_VALIDATION_FAILED
				) {
					const namesForModal = err.response.data.Metadata.dishIds.map(
						(dishId: string) => fetchedDishes.find((item) => item.id === dishId)?.name,
					);
					setDataForModalWithInfo({
						translationKey: 'followingDishesShouldHaveAtLeastOneSelectedRestaurant',
						names: namesForModal,
					});
				} else if (
					axios.isAxiosError(err) &&
					err.response?.data.Type === ServerErrorTypes.COMBO_ITEM_VALIDATION_FAILED
				) {
					const namesForModal = err.response.data.Metadata.comboItems.map(
						(dishId: string) => fetchedComboItems.find((item) => item.id === dishId)?.name,
					);
					setDataForModalWithInfo({
						translationKey: 'followingComboItemsShouldHaveAtLeastOneSelectedRestaurant',
						names: namesForModal,
					});
				} else {
					changeTypeOfModalWindow('error');
				}
			} finally {
				hideLoader();
			}
		}
	}, [dataForSubmit]);

	const handleCloseModal = useCallback(() => {
		changeTypeOfModalWindow(null);
	}, []);

	const handleCloseModalWithInfo = useCallback(() => {
		setDataForModalWithInfo(null);
	}, []);

	return (
		<ModalWrapper isVisible={!!typeOfModalWindow || !!dataForModalWithInfo}>
			<ModalToConfirm
				onConsentEvent={handleReset}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmFieldsClearing'}
				description="cancelSaveInfo"
			/>
			<ModalToConfirm
				onConsentEvent={handleConfirm}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmSubmit'}
				description="saveInfo"
			/>
			<ModalSentSuccessfully
				onConsentEvent={handleReset}
				isVisible={typeOfModalWindow === 'success'}
				description="savedSuccessfully"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'error'}
				description="errorWhileSaving"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'defaultTranslationIsEmpty'}
				description="errorDefaultTranslation"
			/>
			<ModalSentErrorWithInfoForComboGroup
				onConsentEvent={handleCloseModalWithInfo}
				data={dataForModalWithInfo}
			/>
		</ModalWrapper>
	);
};
